import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import celebrityService from '../../services';
import GlassCard from '../ui/GlassCard';
import ActivityTierBadge from '../ui/ActivityTierBadge';
import './ComparisonView.css';

const ComparisonView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [celebrities, setCelebrities] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const celebrityIds = searchParams.get('celebrities')?.split(',') || [];

  useEffect(() => {
    const fetchComparisonData = async () => {
      if (celebrityIds.length < 2) {
        setError('Please select at least 2 celebrities to compare');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await celebrityService.compareCelebrities(celebrityIds);
        setCelebrities(data.celebrities || []);
        setComparison(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchComparisonData();
  }, [celebrityIds.join(',')]);

  if (loading) {
    return (
      <div className="comparison-view loading">
        <div className="loading-spinner"></div>
        <p>Loading comparison...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="comparison-view error">
        <p>{error}</p>
        <button onClick={() => navigate('/celebrities')}>Back to Celebrities</button>
      </div>
    );
  }

  const getTierFromInvestments = (count) => {
    if (count >= 20) return 'Mega Active';
    if (count >= 10) return 'Active';
    if (count >= 5) return 'Moderate';
    return 'Light';
  };

  return (
    <div className="comparison-view">
      <div className="comparison-header">
        <button className="back-button" onClick={() => navigate('/celebrities')}>
          ← Back to Celebrities
        </button>
        <h1 className="comparison-title">Celebrity Comparison</h1>
      </div>

      <div className="celebrities-grid">
        {celebrities.map((celebrity) => (
          <GlassCard key={celebrity.pb_id} className="celebrity-comparison-card">
            <div className="celebrity-header">
              <div className="avatar-medium">
                {celebrity.name.split(' ').map(n => n[0]).join('')}
              </div>
              <h2 className="celebrity-name">{celebrity.name}</h2>
              <ActivityTierBadge 
                tier={getTierFromInvestments(celebrity.total_investments)} 
                size="md" 
              />
            </div>

            <div className="comparison-stats">
              <div className="stat-row">
                <span className="stat-label">Total Investments</span>
                <span className="stat-value">{celebrity.total_investments}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Successful Exits</span>
                <span className="stat-value">{celebrity.total_exits}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Exit Rate</span>
                <span className="stat-value success">
                  {celebrity.total_investments > 0 
                    ? ((celebrity.total_exits / celebrity.total_investments) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Active Investments</span>
                <span className="stat-value">{celebrity.active_investments}</span>
              </div>
            </div>

            {celebrity.top_industries && celebrity.top_industries.length > 0 && (
              <div className="top-industries">
                <h3 className="section-subtitle">Top Industries</h3>
                {celebrity.top_industries.slice(0, 3).map((industry, idx) => (
                  <div key={idx} className="industry-tag">
                    {industry.industry} ({industry.count})
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        ))}
      </div>

      {comparison?.shared_investments && comparison.shared_investments.length > 0 && (
        <GlassCard className="shared-investments-card">
          <h2 className="section-title">
            Shared Investments ({comparison.shared_investments.length})
          </h2>
          <div className="shared-investments-grid">
            {comparison.shared_investments.map((investment, index) => (
              <div key={index} className="shared-investment-item">
                <div className="investment-info">
                  <h3 className="company-name">{investment.company_name}</h3>
                  {investment.industry && (
                    <span className="industry-tag">{investment.industry}</span>
                  )}
                </div>
                <div className="investors-list">
                  {investment.investors.map((investor, idx) => (
                    <span key={idx} className="investor-badge">
                      {investor}
                    </span>
                  ))}
                </div>
                {investment.exit_date && (
                  <span className="exit-indicator">✓ Exited</span>
                )}
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {comparison?.industry_overlap && comparison.industry_overlap.length > 0 && (
        <GlassCard className="industry-overlap-card">
          <h2 className="section-title">Industry Overlap</h2>
          <div className="overlap-chart">
            {comparison.industry_overlap.map((industry, index) => (
              <div key={index} className="overlap-item">
                <div className="overlap-header">
                  <span className="industry-name">{industry.industry}</span>
                  <span className="total-investments">
                    {industry.total_investments} total investments
                  </span>
                </div>
                <div className="investor-breakdown">
                  {industry.investors.map((investor, idx) => (
                    <div key={idx} className="investor-stat">
                      <span className="investor-name">{investor.name}</span>
                      <span className="investor-count">{investor.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {comparison?.co_investment_network && (
        <GlassCard className="network-card">
          <h2 className="section-title">Co-Investment Network</h2>
          <div className="network-stats">
            <div className="network-stat">
              <span className="network-value">
                {comparison.co_investment_network.total_shared_deals}
              </span>
              <span className="network-label">Total Shared Deals</span>
            </div>
            <div className="network-stat">
              <span className="network-value">
                {comparison.co_investment_network.unique_companies}
              </span>
              <span className="network-label">Unique Companies</span>
            </div>
            <div className="network-stat">
              <span className="network-value success">
                {comparison.co_investment_network.shared_exits}
              </span>
              <span className="network-label">Shared Exits</span>
            </div>
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default ComparisonView;